import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BayAPI, BranchAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const BAY_TYPES = ["general", "alignment", "washing", "painting", "electrical", "diagnostic"];

export default function BaysPage() {
    const { permissions, currentBranchId, user } = useAuth();
    const canCreate = permissions.includes("bay:create");
    const canDelete = permissions.includes("bay:delete");

    const [bays, setBays] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    async function load() {
        setLoading(true);
        try {
            const [b, brs] = await Promise.all([
                BayAPI.list({}),
                BranchAPI.list(),
            ]);
            setBays(b.items || []);
            setBranches(brs || []);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const filtered = bays.filter((b) =>
        !filter || b.code.toLowerCase().includes(filter.toLowerCase()) || b.name.toLowerCase().includes(filter.toLowerCase())
    );
    const byBranch = filtered.reduce((acc, b) => {
        (acc[b.branch_id] ||= []).push(b);
        return acc;
    }, {});

    return (
        <div className="space-y-6 animate-fade-in" data-testid="bays-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Bays</h1>
                    <p className="mt-1 text-sm text-zinc-500">Service bays / lifts. Owners can create new bays per branch.</p>
                </div>
                {canCreate && <NewBayDialog branches={branches} userBranchIds={user?.branch_ids || []} onCreated={load} />}
            </header>

            <Card className="p-4">
                <Input placeholder="Filter by code or name…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="bays-filter" />
            </Card>

            {loading ? (
                <Card className="grid place-items-center p-12 text-sm text-zinc-500"><Loader2 className="h-5 w-5 animate-spin" /></Card>
            ) : (
                <div className="space-y-5">
                    {Object.keys(byBranch).length === 0 && (
                        <Card className="grid place-items-center p-12 text-sm text-zinc-500">No bays match.</Card>
                    )}
                    {Object.entries(byBranch).map(([bid, items]) => {
                        const b = branches.find((x) => x.id === bid);
                        return (
                            <Card key={bid} className="p-4">
                                <div className="mb-3 flex items-baseline justify-between">
                                    <h3 className="font-display text-lg font-bold tracking-tight">{b?.code || "—"} <span className="ml-1 text-sm font-normal text-zinc-500">{b?.name}</span></h3>
                                    <span className="font-mono text-xs text-zinc-500">{items.length} {items.length === 1 ? "bay" : "bays"}</span>
                                </div>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                                    {items.map((bay) => (
                                        <div key={bay.id} className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800" data-testid={`bay-row-${bay.id}`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="font-mono text-sm font-semibold">{bay.code}</div>
                                                    <div className="truncate text-xs text-zinc-500">{bay.name}</div>
                                                </div>
                                                {canDelete && (
                                                    <Button size="icon" variant="ghost" onClick={async () => {
                                                        if (!window.confirm(`Delete bay ${bay.code}?`)) return;
                                                        try { await BayAPI.remove(bay.id); toast.success("Bay deleted"); load(); }
                                                        catch (e) { toast.error(extractError(e)); }
                                                    }} data-testid={`bay-delete-${bay.id}`}>
                                                        <Trash2 className="h-4 w-4 text-rose-500" />
                                                    </Button>
                                                )}
                                            </div>
                                            <div className="mt-1 inline-flex rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                                                {bay.bay_type}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function NewBayDialog({ branches, userBranchIds, onCreated }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const eligible = branches.filter((b) => userBranchIds.length === 0 || userBranchIds.includes(b.id));
    const [form, setForm] = useState({ branch_id: eligible[0]?.id || "", code: "", name: "", bay_type: "general" });

    useEffect(() => {
        if (eligible.length && !form.branch_id) setForm((f) => ({ ...f, branch_id: eligible[0].id }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branches.length]);

    async function submit() {
        setBusy(true);
        try {
            await BayAPI.create({ ...form, code: form.code.toUpperCase() });
            toast.success("Bay created");
            setOpen(false);
            setForm({ branch_id: form.branch_id, code: "", name: "", bay_type: "general" });
            onCreated && onCreated();
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="bay-new-btn"><Plus className="mr-1 h-4 w-4" /> New bay</Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle>New bay</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <FieldDialog label="Branch"><select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="bay-branch">
                        {eligible.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
                    </select></FieldDialog>
                    <FieldDialog label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="BAY-5" className="font-mono uppercase" data-testid="bay-code" /></FieldDialog>
                    <FieldDialog label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bay-name" /></FieldDialog>
                    <FieldDialog label="Type"><select value={form.bay_type} onChange={(e) => setForm({ ...form, bay_type: e.target.value })}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="bay-type">
                        {BAY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select></FieldDialog>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !form.branch_id || !form.code || !form.name}
                        className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="bay-save">
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldDialog({ label, children }) {
    return <div className="space-y-1.5"><Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</Label>{children}</div>;
}
